import { useState, useRef, useEffect } from 'react';
import api from '../api';

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.text }));
      const result = await api.chat(userMsg, history);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: result.response,
        queries: result.queriesRun,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', text: err.message }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const SUGGESTIONS = [
    'What are our top 5 projects by contract revenue?',
    'Which projects have negative gross profit?',
    'Show me all pending change orders over $50K',
    'What is our total backlog by project manager?',
    'Which projects are over 90% complete?',
    'How much retainage is held across all projects?',
  ];

  return (
    <div className="page chat-page">
      <div className="page-header">
        <div>
          <h1>Ledger AI</h1>
          <p className="page-subtitle">Ask questions about your projects, financials, bids, and contracts</p>
        </div>
      </div>

      <div className="chat-container">
        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="chat-welcome">
              <div className="chat-welcome-title">What can I help you find?</div>
              <p className="chat-welcome-desc">I can query Procore project data, BidBoard bids, and financial reports across all CM Corp databases.</p>
              <div className="chat-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} className="chat-suggestion" onClick={() => setInput(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`chat-msg chat-msg-${msg.role}`}>
              <div className="chat-msg-label">{msg.role === 'user' ? 'You' : msg.role === 'error' ? 'Error' : 'Ledger AI'}</div>
              <div className="chat-msg-body">
                {msg.text.split('\n').map((line, j) => <p key={j}>{line || '\u00A0'}</p>)}
              </div>
              {msg.queries > 0 && <div className="chat-msg-meta">{msg.queries} database {msg.queries === 1 ? 'query' : 'queries'} executed</div>}
            </div>
          ))}

          {loading && (
            <div className="chat-msg chat-msg-assistant">
              <div className="chat-msg-label">Ledger AI</div>
              <div className="chat-msg-body chat-typing">Querying databases...</div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="chat-input-form">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask about projects, financials, bids..."
            className="chat-input"
            disabled={loading}
            autoFocus
          />
          <button type="submit" className="btn btn-primary" disabled={loading || !input.trim()}>
            {loading ? 'Thinking...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}
